import { LoaderBar, SendEmailModal } from 'components'
import { GetCustomerList, GetTempleteList, useEmptyAccountTemplateRedirect } from 'hooks'
import { useNavigate } from 'react-router-dom'
import { TemplateList, TemplateNameModal, TemplateListHeader } from 'page-components'
import { useState } from 'react'
import { InspectionService, TemplateService } from 'utility'
import { useNotification } from 'hooks/toast'

/**
 * MyTemplates query param TemplateInternalType for Daily Defect Report.
 * Must match what the API / template admin uses for this product line.
 */
export const DAILY_DEFECT_REPORT_TEMPLATE_INTERNAL_TYPE = 'R'

export const DailyDefectReportTemplateListScreen = () => {
    const navigate = useNavigate()

    const {
        data,
        loading,
        filter,
        pageChanged,
        totalCount,
        setData,
        filterChanged,
    } = GetTempleteList(null, DAILY_DEFECT_REPORT_TEMPLATE_INTERNAL_TYPE)
    const { successToast } = useNotification()
    const [processing, setProcessing] = useState('')
    const [search, setSearch] = useState('')
    const [emailModalOpen, setEmailModalOpen] = useState(false)
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [showTemplateNameModal, setShowTemplateNameModal] = useState(false)
    const [creating, setCreating] = useState(false)
    const { data: customerList, loading: customerLoading, refresh: refreshCustomerList } =
        GetCustomerList({ CallFrom: 1 })

    const headerText = 'Mimaki Production Template list'

    useEmptyAccountTemplateRedirect({ loading, data })

    const handleSearchClick = () => {
        const trimmedSearch = search.trim()

        if (trimmedSearch === '') {
            filterChanged({ SearchText: '' })
            return
        }

        filterChanged({ SearchText: trimmedSearch })
    }

    const handleKeyPress = e => {
        if (e.key === 'Enter') {
            handleSearchClick()
        }
    }

    const onRemove = async item => {
        try {
            setProcessing('Processing')
            await TemplateService.removeTemplate(item.TemplateId)
            setData(data.filter(ele => ele.TemplateId !== item.TemplateId))
            successToast('Template deleted successfully')
        } catch (error) {
            console.log(error)
        } finally {
            setProcessing('')
        }
    }

    const startInspection = async (item, list) => {
        try {
            const result = await InspectionService.copyInspection({
                TemplateId: item.TemplateId,
                customerId: list.toString(),
            })
            navigate(`/app/templates/inspection/${result?.AuditTemplateId}`)
        } catch (error) {
            console.log(error)
        } finally {
            setProcessing('')
        }
    }

    const handleSendEmail = template => {
        setSelectedTemplate(template)
        setEmailModalOpen(true)
    }

    const handleEmailSuccess = () => {
        // Optional: Show success message
    }

    const handleOpenCreateModal = () => {
        setShowTemplateNameModal(true)
    }

    const handleCreateTemplate = async templateName => {
        setCreating(true)
        try {
            const result = await TemplateService.create({
                TemplateId: '-1',
                TemplateName: templateName,
                LoginSource: 1,
                TemplateAccess: 'Internal',
            })
            navigate('/app/templates/create', { state: { template: result } })
        } catch (error) {
            console.error('Failed to create template:', error)
        } finally {
            setCreating(false)
            setShowTemplateNameModal(false)
        }
    }

    return (
        <>
            <TemplateListHeader
                search={search}
                setSearch={setSearch}
                handleSearchClick={handleSearchClick}
                handleKeyPress={handleKeyPress}
                onCreateClick={handleOpenCreateModal}
                headerText={headerText}
            />
            <div className="my-3">
                {(processing || loading) && (
                    <LoaderBar content={processing || 'Loading'} />
                )}
                <TemplateList
                    data={data}
                    pageChanged={pageChanged}
                    totalCount={totalCount}
                    filter={filter}
                    primaryButtonLabel="Select"
                    customerList={customerList}
                    customerLoading={customerLoading}
                    onRemove={onRemove}
                    startInspection={startInspection}
                    onSendEmail={handleSendEmail}
                    refreshCustomerList={refreshCustomerList}
                    sortBy={filter.SortBy}
                    onSortChange={value =>
                        filterChanged({ SortBy: value, PageNumber: 1 })
                    }
                />
            </div>
            {emailModalOpen && selectedTemplate && (
                <SendEmailModal
                    open={emailModalOpen}
                    onClose={() => setEmailModalOpen(false)}
                    template={selectedTemplate}
                    onSuccess={handleEmailSuccess}
                />
            )}
            <TemplateNameModal
                open={showTemplateNameModal}
                onClose={() => setShowTemplateNameModal(false)}
                onSubmit={handleCreateTemplate}
                loading={creating}
            />
        </>
    )
}
